var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { action, computed, observable } from 'mobx';
import CollectionsAPI from '@/collection/api/CollectionsAPI';
import CommentAPI from '@/collection/api/CommentAPI';
import { flattenTree } from '@/common/components/Tree/Tree.utils';
import useStore from '@/common/hooks/useStore';
import Notifier from '@/common/services/Notifier';
import { DriveAPI } from '@/drive/api/DriveAPI';
import { DriveType } from '@/drive/entities/DriveType';
import { fromDriveNode } from '@/drive/types/driveTreeItem';
import ForwardDestinationStorage from '@/forwarding/services/ForwardDestinationStorage';
import { ForwardNotificationService } from '@/forwarding/services/ForwardNotificationService';
import { ForwardDestinationType, } from '@/forwarding/types/ForwardDestinationListData';
class ForwardModalStore {
    userStore;
    driveCollectionsStore;
    driveItemModalStore;
    driveAPI;
    spectrumDriveAPI;
    collectionsAPI;
    commentsAPI;
    hasAnyDestinations = false;
    driveTree = null;
    baseDestinations = null;
    filter = '';
    config = null;
    isAddToCollectionSubmitting = false;
    get isSubmitting() {
        return this.driveItemModalStore.isSubmitting || this.isAddToCollectionSubmitting;
    }
    get allDestinations() {
        const userNotForwardedAnythingYet = this.baseDestinations && this.baseDestinations.length === 0;
        if (userNotForwardedAnythingYet) {
            return [];
        }
        if (!this.baseDestinations || !this.driveCollectionsStore.collections || !this.driveTree || !this.config) {
            return null;
        }
        const foldersMap = flattenTree(this.driveTree.map(fromDriveNode))
            .map((item) => item.original)
            .reduce((acc, item) => {
            acc.set(item.id, item);
            return acc;
        }, new Map());
        const collectionsMap = this.driveCollectionsStore.collections.reduce((acc, item) => {
            acc.set(item.id, item);
            return acc;
        }, new Map());
        return this.baseDestinations.flatMap((destination) => {
            const data = this.toDestinationListData(destination, foldersMap, collectionsMap, this.config);
            return data ? [data] : [];
        });
    }
    get destinations() {
        if (!this.allDestinations) {
            return null;
        }
        const filter = this.filter.trim().toLowerCase();
        if (filter.length === 0) {
            return this.allDestinations;
        }
        return this.allDestinations.filter((d) => d.title.includes(filter));
    }
    constructor(userStore, driveCollectionsStore, driveItemModalStore, driveAPI = new DriveAPI(), spectrumDriveAPI = new DriveAPI(DriveType.CutwiseSpectrum), collectionsAPI = new CollectionsAPI(), commentsAPI = new CommentAPI()) {
        this.userStore = userStore;
        this.driveCollectionsStore = driveCollectionsStore;
        this.driveItemModalStore = driveItemModalStore;
        this.driveAPI = driveAPI;
        this.spectrumDriveAPI = spectrumDriveAPI;
        this.collectionsAPI = collectionsAPI;
        this.commentsAPI = commentsAPI;
    }
    changeFilter = (filter) => {
        this.filter = filter;
    };
    refresh = async (config) => {
        this.config = config;
        const destinations = ForwardDestinationStorage.get();
        if (!this.userStore.isAuthorized || destinations.length === 0) {
            this.baseDestinations = [];
            this.driveTree = [];
            this.hasAnyDestinations = false;
            return;
        }
        this.baseDestinations = destinations;
        this.hasAnyDestinations = true;
        if (this.userStore.user?.isFlagAllowed('allowDrive')) {
            this.driveTree = await this.driveAPI.fetchTree();
        }
        else {
            this.driveTree = [];
        }
        return this.driveCollectionsStore.refresh();
    };
    reset = () => {
        // driveTree is not reset to allow faster display of the last destinations list on future forwarding
        this.filter = '';
        this.baseDestinations = null;
        this.config = null;
    };
    submit = async (config, form, destination) => {
        if (destination.type === ForwardDestinationType.Folder) {
            return this.driveItemModalStore.submit(config, form, destination.id);
        }
        else {
            await this.addToCollection(config, form, destination.id);
            return { success: true };
        }
    };
    addToSpectrum = async (config, form) => {
        const reqData = config.getSpectrumAddReqData();
        if (!reqData) {
            return { success: false };
        }
        const mappedReqData = reqData.map((item) => ({
            ...item,
            ...config.formToRequestData(form),
            parent: null,
        }));
        const res = await this.spectrumDriveAPI.batchCreate(mappedReqData);
        if (res.hasErrors()) {
            res.getErrors().forEach((error) => Notifier.error(error));
            return { success: false };
        }
        else {
            ForwardNotificationService.addToSpectrumSuccess(res.getItems()[0].id);
            return { success: true };
        }
    };
    addToCollection = async (config, form, parentId) => {
        this.isAddToCollectionSubmitting = true;
        ForwardDestinationStorage.add({ id: parentId, type: ForwardDestinationType.Collection });
        try {
            await Promise.all([
                this.collectionsAPI.addToCollection(parentId, config.getCollectionReqData().ids),
                this.createCommentIfChangedByUser(config, form, parentId),
            ]);
            ForwardNotificationService.addedToCollectionSuccess(parentId);
        }
        finally {
            this.isAddToCollectionSubmitting = false;
        }
    };
    createCommentIfChangedByUser = async (config, form, parentId) => {
        const params = config.getCollectionReqData();
        if (params?.ids.length !== 1) {
            return;
        }
        const trimmedTitle = form.title.trim();
        const trimmedDescription = form.description.trim();
        const shouldSaveTitle = config.getForm().title.trim() !== trimmedTitle && trimmedTitle.length > 0;
        const shouldSaveDescription = config.getForm().description.trim() !== trimmedDescription && form.description.length > 0;
        const savedCommentData = [];
        if (shouldSaveTitle) {
            savedCommentData.push(`<p><bold>${trimmedTitle}</bold></p>`);
        }
        if (shouldSaveDescription) {
            savedCommentData.push(`<p>${trimmedDescription}</p>`);
        }
        if (savedCommentData.length > 0) {
            await this.commentsAPI.createCollectionComment(parentId, params.ids[0], savedCommentData.join(''));
        }
    };
    toDestinationListData(destination, foldersMap, collectionsMap, config) {
        if (destination.type === ForwardDestinationType.Folder && !destination.id) {
            return { type: ForwardDestinationType.Folder, id: null, title: 'Drive Root Folder' };
        }
        const folder = foldersMap.get(destination.id);
        const collection = collectionsMap.get(destination.id);
        const isFolderDestination = destination.type === ForwardDestinationType.Folder && folder;
        const isCollectionDestination = destination.type === ForwardDestinationType.Collection && collection;
        if (isFolderDestination) {
            return {
                id: destination.id,
                type: ForwardDestinationType.Folder,
                title: folder.title,
            };
        }
        else {
            if (isCollectionDestination && config.addToCollectionEnabled(collection.type)) {
                return {
                    id: destination.id,
                    type: ForwardDestinationType.Collection,
                    title: collection.title,
                };
            }
        }
        return null;
    }
}
__decorate([
    observable
], ForwardModalStore.prototype, "hasAnyDestinations", void 0);
__decorate([
    observable
], ForwardModalStore.prototype, "driveTree", void 0);
__decorate([
    observable
], ForwardModalStore.prototype, "baseDestinations", void 0);
__decorate([
    observable
], ForwardModalStore.prototype, "filter", void 0);
__decorate([
    observable
], ForwardModalStore.prototype, "config", void 0);
__decorate([
    observable
], ForwardModalStore.prototype, "isAddToCollectionSubmitting", void 0);
__decorate([
    computed
], ForwardModalStore.prototype, "isSubmitting", null);
__decorate([
    computed
], ForwardModalStore.prototype, "allDestinations", null);
__decorate([
    computed
], ForwardModalStore.prototype, "destinations", null);
__decorate([
    action
], ForwardModalStore.prototype, "changeFilter", void 0);
__decorate([
    action
], ForwardModalStore.prototype, "refresh", void 0);
__decorate([
    action
], ForwardModalStore.prototype, "reset", void 0);
export const useForwardModalStore = () => useStore(ForwardModalStore);
export default ForwardModalStore;
